<template>
    <div class="sequencing-analysis">
      <b-card no-body class="mb-3">
        <b-card-header class="bg-primary text-white">
          <b-row>
            <b-col>
              <h3 class="mb-0">{{ $t('Plan Sequencing Analysis') }}</h3>
            </b-col>
            <b-col cols="auto">
              <b-button size="sm" variant="light" @click="refreshAnalysis" :disabled="isAnalyzing">
                <font-awesome-icon :icon="['fa', 'sync']" :spin="isAnalyzing" />
                {{ isAnalyzing ? $t('Analyzing...') : $t('Analyze') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-header>
        
        <b-card-body v-if="isAnalyzing">
          <div class="text-center py-5">
            <b-spinner variant="primary" label="Analyzing plans..."></b-spinner>
            <p class="mt-3">{{ $t('Analyzing your plans for potential sequencing issues...') }}</p>
          </div>
        </b-card-body>
        
        <div v-else-if="analysisResults">
          <!-- Analysis Results Summary -->
          <b-list-group flush>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              {{ $t('Critical Issues') }}
              <b-badge variant="danger" pill>{{ analysisResults.analysis.criticalIssues.length }}</b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              {{ $t('Sequencing Errors') }}
              <b-badge variant="warning" pill>{{ analysisResults.analysis.sequencingErrors.length }}</b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              {{ $t('Spatial Conflicts') }}
              <b-badge variant="info" pill>{{ analysisResults.analysis.spatialConflicts.length }}</b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              {{ $t('Inspection Issues') }}
              <b-badge variant="primary" pill>{{ analysisResults.analysis.inspectionIssues.length }}</b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              {{ $t('Resource Conflicts') }}
              <b-badge variant="secondary" pill>{{ analysisResults.analysis.resourceConflicts.length }}</b-badge>
            </b-list-group-item>
          </b-list-group>
          
          <!-- Detailed Analysis Tabs -->
          <b-card-body class="pt-0">
            <b-tabs v-model="activeTab" card>
              <!-- Critical Issues Tab -->
              <b-tab :title="$t('Critical Issues')" :disabled="!analysisResults.analysis.criticalIssues.length">
                <conflict-list 
                  :conflicts="analysisResults.analysis.criticalIssues" 
                  :type="'critical'"
                  @resolve="resolveConflict('criticalIssues', $event)"
                />
              </b-tab>
              
              <!-- Sequencing Errors Tab -->
              <b-tab :title="$t('Sequencing Errors')" :disabled="!analysisResults.analysis.sequencingErrors.length">
                <conflict-list 
                  :conflicts="analysisResults.analysis.sequencingErrors" 
                  :type="'sequencing'"
                  @resolve="resolveConflict('sequencingErrors', $event)"
                />
              </b-tab>
              
              <!-- Spatial Conflicts Tab -->
              <b-tab :title="$t('Spatial Conflicts')" :disabled="!analysisResults.analysis.spatialConflicts.length">
                <conflict-list 
                  :conflicts="analysisResults.analysis.spatialConflicts" 
                  :type="'spatial'"
                  @resolve="resolveConflict('spatialConflicts', $event)"
                />
              </b-tab>
              
              <!-- Inspection Issues Tab -->
              <b-tab :title="$t('Inspection Issues')" :disabled="!analysisResults.analysis.inspectionIssues.length">
                <conflict-list 
                  :conflicts="analysisResults.analysis.inspectionIssues" 
                  :type="'inspection'"
                  @resolve="resolveConflict('inspectionIssues', $event)"
                />
              </b-tab>
              
              <!-- Resource Conflicts Tab -->
              <b-tab :title="$t('Resource Conflicts')" :disabled="!analysisResults.analysis.resourceConflicts.length">
                <conflict-list 
                  :conflicts="analysisResults.analysis.resourceConflicts" 
                  :type="'resource'"
                  @resolve="resolveConflict('resourceConflicts', $event)"
                />
              </b-tab>
              
              <!-- Recommendations Tab -->
              <b-tab :title="$t('Recommendations')" :disabled="!analysisResults.analysis.recommendations.length">
                <recommendation-list :recommendations="analysisResults.analysis.recommendations" />
              </b-tab>
            </b-tabs>
          </b-card-body>
        </div>
        
        <b-card-body v-else>
          <div class="text-center py-4">
            <font-awesome-icon :icon="['fa', 'search']" size="3x" class="text-muted mb-3" />
            <p>{{ $t('No analysis results yet. Click Analyze to check for sequencing issues.') }}</p>
          </div>
        </b-card-body>
      </b-card>
      
      <!-- Configuration Modal -->
      <b-modal
        v-model="showConfigModal"
        :title="$t('Analysis Configuration')"
        @ok="runAnalysis"
      >
        <b-form>
          <b-form-group :label="$t('Include Historical Data')">
            <b-form-checkbox v-model="config.includeHistoricalData">
              {{ $t('Include historical plans for context') }}
            </b-form-checkbox>
          </b-form-group>
          
          <b-form-group :label="$t('Lookback Period (days)')" v-if="config.includeHistoricalData">
            <b-form-input
              v-model.number="config.lookbackDays"
              type="number"
              min="1"
              max="90"
            ></b-form-input>
          </b-form-group>
          
          <b-form-group :label="$t('Analysis Depth')">
            <b-form-select v-model="config.analysisDepth" :options="analysisDepthOptions"></b-form-select>
          </b-form-group>
        </b-form>
      </b-modal>
      
      <!-- Resolution Modal -->
      <b-modal
        v-model="showResolutionModal"
        :title="$t('Resolve Conflict')"
        @ok="confirmResolveConflict"
      >
        <b-form>
          <b-form-group :label="$t('Resolution Notes')">
            <b-form-textarea
              v-model="resolutionNotes"
              rows="3"
              :placeholder="$t('Enter details about how this conflict was resolved...')"
            ></b-form-textarea>
          </b-form-group>
        </b-form>
      </b-modal>
    </div>
  </template>
  
  <script>
  import { mapGetters } from 'vuex';
  import ConflictList from '@/components/ConflictList.vue';
  import RecommendationList from '@/components/RecommendationList.vue';
  
  export default {
    name: 'SequencingAnalysis',
    components: {
      ConflictList,
      RecommendationList
    },
    data() {
      return {
        isAnalyzing: false,
        analysisResults: null,
        activeTab: 0,
        showConfigModal: false,
        showResolutionModal: false,
        config: {
          includeHistoricalData: false,
          lookbackDays: 14,
          analysisDepth: 'standard'
        },
        analysisDepthOptions: [
          { value: 'basic', text: this.$t('Basic - Quick scan for major issues') },
          { value: 'standard', text: this.$t('Standard - Comprehensive analysis') },
          { value: 'detailed', text: this.$t('Detailed - In-depth analysis with more context') }
        ],
        currentConflict: null,
        currentSection: null,
        resolutionNotes: ''
      };
    },
    computed: {
      ...mapGetters({
        user: 'user',
        project: 'project'
      }),
      hasConflicts() {
        if (!this.analysisResults || !this.analysisResults.analysis) return false;
        
        const analysis = this.analysisResults.analysis;
        return (
          analysis.criticalIssues.length +
          analysis.sequencingErrors.length +
          analysis.spatialConflicts.length +
          analysis.inspectionIssues.length +
          analysis.resourceConflicts.length
        ) > 0;
      }
    },
    methods: {
        $t(text) {
    return text;
  },
      refreshAnalysis() {
        this.showConfigModal = true;
      },
      
      async runAnalysis() {
        this.isAnalyzing = true;
        this.analysisResults = null;
        
        try {
          // Fetch recent plans for the analysis
          const plansResponse = await this.$functions.httpsCallable('getRecentPlans')({
            projectId: this.project.id,
            // Use current date as the filter, adjust as needed
            startDate: new Date().toISOString().split('T')[0]
          });
          
          const plans = plansResponse.data.plans;
          
          if (!plans || plans.length === 0) {
            this.$bvToast.toast(this.$t('No plans found for analysis'), {
              title: this.$t('No Plans'),
              variant: 'warning',
              solid: true
            });
            this.isAnalyzing = false;
            return;
          }
          
          // Call the cloud function to analyze the plans
          const analysisConfig = {
            ...this.config,
            projectId: this.project.id
          };
          
          const response = await this.$functions.httpsCallable('analyzeSequencing')({
            plans,
            config: analysisConfig,
            projectId: this.project && this.project.id,
          });
          
          this.analysisResults = response.data;
          
          // Set the active tab to the first one with conflicts
          this.setInitialActiveTab();
          
        } catch (error) {
          console.error('Error analyzing plans:', error);
          this.$bvToast.toast(this.$t('Error analyzing plans. Please try again.'), {
            title: this.$t('Analysis Error'),
            variant: 'danger',
            solid: true
          });
        } finally {
          this.isAnalyzing = false;
        }
      },
      
      setInitialActiveTab() {
        const analysis = this.analysisResults.analysis;
        
        // Set active tab to the first category with issues
        if (analysis.criticalIssues.length > 0) {
          this.activeTab = 0;
        } else if (analysis.sequencingErrors.length > 0) {
          this.activeTab = 1;
        } else if (analysis.spatialConflicts.length > 0) {
          this.activeTab = 2;
        } else if (analysis.inspectionIssues.length > 0) {
          this.activeTab = 3;
        } else if (analysis.resourceConflicts.length > 0) {
          this.activeTab = 4;
        } else if (analysis.recommendations.length > 0) {
          this.activeTab = 5;
        }
      },
      
      resolveConflict(section, conflict) {
        this.currentSection = section;
        this.currentConflict = conflict;
        this.resolutionNotes = '';
        this.showResolutionModal = true;
      },
      
      async confirmResolveConflict() {
        try {
          // Update the conflict status in Firestore
          await this.$functions.httpsCallable('updateConflictStatus')({
            projectId: this.project && this.project.id,
            analysisId: this.analysisResults.id,
            sectionId: this.currentSection,
            conflictId: this.currentConflict.id,
            newStatus: 'resolved',
            resolution: this.resolutionNotes
          });
          
          // Update local state
          const conflictIndex = this.analysisResults.analysis[this.currentSection]
            .findIndex(c => c.id === this.currentConflict.id);
          
          if (conflictIndex !== -1) {
            this.analysisResults.analysis[this.currentSection][conflictIndex].status = 'resolved';
            this.analysisResults.analysis[this.currentSection][conflictIndex].resolution = this.resolutionNotes;
          }
          
          this.$bvToast.toast(this.$t('Conflict marked as resolved'), {
            title: this.$t('Success'),
            variant: 'success',
            solid: true
          });
          
        } catch (error) {
          console.error('Error resolving conflict:', error);
          this.$bvToast.toast(this.$t('Error updating conflict status'), {
            title: this.$t('Update Error'),
            variant: 'danger',
            solid: true
          });
        }
      }
    },
    mounted() {
      // Optionally auto-run analysis when component mounts
      // this.refreshAnalysis();
    }
  };
  </script>
  
  <style scoped>
  .sequencing-analysis {
    margin-bottom: 2rem;
  }
  </style>